import { Fragment, useState } from "react";

import { Alert, Box, Button, Snackbar, Stack, Typography } from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ClientAppEvent, QUOTES_ELEMENT_ID } from "constants/index";
import { useDisclosure } from "hooks";

import { getShipmentProgressSummary } from "../../../ShipmentProgress";
import {
  AddPartnersToShipment,
  PartnersAddedToast,
} from "../SideBarMenu/components/AddPartnersToShipment";
import {
  copyShipmentInfo,
  TellMoreCarriersModal,
} from "../TellMoreCarriersModal";

import type { TypographyProps } from "@mui/material";

interface Props {
  shipment: ShipperCondensedShipment | undefined;
  progress: ShipmentProgress | null;
  progressError: string | null;
  headerVariant: TypographyProps["variant"];
  onOpenNotifiedCarriers: VoidFunction;
}

// Evotrux3 primary button, small size.
const ACTION_BUTTON_SX = {
  textTransform: "none",
  fontSize: 13,
  fontWeight: 500,
  borderRadius: "4px",
  minHeight: 30,
  px: 1.5,
  whiteSpace: "nowrap",
  boxShadow: "none",
  backgroundColor: "#1976D2",
  "&:hover": { backgroundColor: "#1565C0", boxShadow: "none" },
} as const;

type ShareAction = "TELL_MORE_CARRIERS" | "ADD_CARRIER_PARTNERS" | null;

/*
  How the shipper can get the load to more carriers, while carriers can still
  quote. A copied link only works for carriers who can open the shipment:
  - Public: any carrier on Evotrux, so the shipper copies a message to send.
  - Private to selected partners: only those partners, so more go through
    Add Carrier Partners, which also emails them. Owner only, as in the side
    menu.
  - Private to all partners: every partner already has it and nobody else can
    open it, so there is nothing to offer.
*/
function getShareAction(
  shipment: ShipperCondensedShipment | undefined,
): ShareAction {
  if (shipment?.shipment_status !== "ACTIVE") {
    return null;
  }
  if (!shipment.is_private) {
    return "TELL_MORE_CARRIERS";
  }
  if (
    shipment.network_notification_type === "PRIVATE_SHIPMENT" &&
    shipment.access_type === "OWNER"
  ) {
    return "ADD_CARRIER_PARTNERS";
  }
  return null;
}

function Count({ value }: { value: number }) {
  return (
    <Box component="span" sx={{ fontSize: 15, fontWeight: 700 }}>
      {value}
    </Box>
  );
}

/*
  The Quotes heading, with the shipment's notification counts and the way to
  get the load to more carriers on the same line, so a shipper sees the work
  being done for them right where their quotes arrive.
*/
export function QuotesSectionHeader(props: Props) {
  const {
    shipment,
    progress,
    progressError,
    headerVariant,
    onOpenNotifiedCarriers,
  } = props;
  const posthog = usePostHog();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: addPartnersOpen,
    onOpen: onOpenAddPartners,
    onClose: onCloseAddPartners,
  } = useDisclosure();
  const [copied, setCopied] = useState(false);
  const [partnersAdded, setPartnersAdded] = useState(false);
  // Mounted from the first open on, not before: it loads the partner network
  // when mounted.
  const [addPartnersMounted, setAddPartnersMounted] = useState(false);

  const handleOpenAddPartners = () => {
    setAddPartnersMounted(true);
    onOpenAddPartners();
  };

  // Only progress for this shipment: the page can switch shipments while the
  // previous one's progress is still in state.
  const summary =
    shipment &&
    progress &&
    !progressError &&
    progress.shipment === shipment.info.id
      ? getShipmentProgressSummary(shipment, progress)
      : null;
  const shareAction = getShareAction(shipment);

  const eventProperties = shipment
    ? {
        shipment_id: shipment.info.id,
        shipper_company_id: shipment.shipment_company.id,
        notified_count: summary?.notified.count,
        quote_count: summary ? progress?.quote_count : undefined,
      }
    : {};

  const handleOpen = () => {
    posthog?.capture(ClientAppEvent.TELL_MORE_CARRIERS_OPENED, eventProperties);
    onOpen();
  };

  const handleCopy = async () => {
    if (!shipment) return;
    try {
      await copyShipmentInfo(shipment);
      onClose();
      setCopied(true);
      posthog?.capture(
        ClientAppEvent.TELL_MORE_CARRIERS_SHIPMENT_INFO_COPIED,
        eventProperties,
      );
    } catch {
      // No-op: clipboard write can fail in non-secure contexts or when permission is denied.
    }
  };

  return (
    <>
      <Stack
        id={QUOTES_ELEMENT_ID}
        direction="row"
        mt="1rem"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
        spacing={2}
      >
        <Typography variant={headerVariant}>Quotes</Typography>
        <Stack direction="row" alignItems="center" spacing={2}>
          {summary && (
            // No "Matches Loading.." here: it shows whenever the lane match
            // count is 0, including when matching finished and found nobody.
            <Typography component="div" sx={{ fontSize: 14, color: "#212121" }}>
              <Box
                component="button"
                type="button"
                onClick={onOpenNotifiedCarriers}
                sx={{
                  background: "none",
                  border: "none",
                  p: 0,
                  cursor: "pointer",
                  font: "inherit",
                  fontSize: 14,
                  color: "#1976D2",
                  verticalAlign: "baseline",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                <Count value={summary.notified.count} />{" "}
                {summary.notified.label}
              </Box>
              {summary.others.map((item) => (
                <Fragment key={item.label}>
                  <Box component="span" sx={{ mx: 0.75 }}>
                    •
                  </Box>
                  <Count value={item.count} /> {item.label}
                </Fragment>
              ))}
            </Typography>
          )}
          {shareAction === "TELL_MORE_CARRIERS" && (
            <Button
              variant="contained"
              size="small"
              disableElevation
              onClick={handleOpen}
              sx={ACTION_BUTTON_SX}
            >
              Tell more carriers
            </Button>
          )}
          {shareAction === "ADD_CARRIER_PARTNERS" && (
            <Button
              variant="contained"
              size="small"
              disableElevation
              onClick={handleOpenAddPartners}
              sx={ACTION_BUTTON_SX}
            >
              Add carrier partners
            </Button>
          )}
        </Stack>
      </Stack>

      {shipment && (
        <TellMoreCarriersModal
          open={isOpen}
          onClose={onClose}
          onCopy={handleCopy}
          shipment={shipment}
        />
      )}

      {/* The side menu's own pop-up, so both entry points behave the same. */}
      {addPartnersMounted && (
        <AddPartnersToShipment
          modalOpen={addPartnersOpen}
          onModalClose={onCloseAddPartners}
          onPartnersAdded={() => setPartnersAdded(true)}
        />
      )}
      <PartnersAddedToast
        open={partnersAdded}
        onClose={() => setPartnersAdded(false)}
      />

      <Snackbar
        open={copied}
        autoHideDuration={3000}
        onClose={() => setCopied(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setCopied(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Shipment info copied to clipboard.
        </Alert>
      </Snackbar>
    </>
  );
}
